import type { ComponentProps } from 'react';
import { cn } from '@/lib/utils';

/**
 * The HyperTek prototype's input (h-prototype `components/ui/input.tsx`), class for class, with the product
 * deltas below and nothing else:
 *
 * - `h-11` under a thumb, the prototype's `h-9` (36 px) under a fine pointer: the touch floor applies to fields
 *   as much as to buttons (styles.css gives every field `min-height: 44px` under `pointer: coarse` as well).
 * - `min-w-0`, so a field in a flex row can shrink below its intrinsic width instead of pushing a 360 px
 *   screen sideways.
 * - An invalid field is red, and stays red while it has focus: `aria-invalid:focus-visible:*` is one
 *   attribute more specific than `focus-visible:*`, so it wins whatever order Tailwind emits the variants in.
 *   (Textarea and NativeSelect carry the same pair.)
 * - A read-only field (an invitation link, the email a reset link belongs to) reads as not editable.
 */
function Input({ className, type, ...props }: ComponentProps<'input'>) {
  return (
    <input
      type={type}
      data-slot="input"
      className={cn(
        'flex h-11 pointer-fine:h-9 w-full min-w-0 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
        'aria-invalid:border-destructive read-only:bg-[hsl(var(--h-muted))]',
        className,
      )}
      {...props}
    />
  );
}

export { Input };
